import { FilterSection } from '@/app/features/reports/components/filter-section';
import { FilterTypes, SelectedFilters } from '@/app/features/reports/types/filters';
import ToggleWithBadge from '@/components/raytd/toggle-with-badge';
import { cn } from '@/lib/utils';
import React, { useCallback, useMemo } from 'react';
import BulkEditDropdown from './bulk-actions-button';
import EditModeComponent from './edit-component';
import { ExcludedTestsBadge } from './excluded-tests-badge';
import FilterInput from './filter-input';
import useReportViewerContext, { useReportViewContext } from './report-viewer-context';
import TestErrorPill, { TestErrorPillButton } from '@/app/features/reports/components/test-error-icons';
interface FilteredTestCountProps {
    showLabel?: boolean;
    className?: string;
}

export function FilteredTestCount({ showLabel = false, className }: FilteredTestCountProps) {
    const { visibleTestCount, reportTotal } = useReportViewContext();
    return <span className={cn(`ml-1 text-xxs text-zinc-600 tracking-normal font-normal`, className)}>{showLabel ? `${visibleTestCount} / ${reportTotal} Tests` : `(${visibleTestCount}/${reportTotal})`}</span>;
}

function ToolbarSlot({ children }: { children: React.ReactNode }) {
    return <div className='flex flex-row items-center gap-4 pb-2 border-b last:border-b-0 border-zinc-200 h-10'>
        {children}
    </div>
}

interface ReportFilterBarProps {
    handleFilterChange: (value: string) => void;
    missingSegments: number;
    missingDataCount: number;
    scrollToTest: (id: number) => void;
}

const ReportFilterBar: React.FC<ReportFilterBarProps> = ({
    handleFilterChange,
    scrollToTest,
}) => {

    const {
        filteredData,
        filters,
        missingDataCount,
        missingSegments,
        groupMode,

    } = useReportViewContext();

    const {
        showMissingData,
        setShowMissingData,
        showFastFillQA,
        setShowFastFillQA,
        showStatus,
        setShowStatus,
    } = useReportViewerContext();

    const {
        selectedFilters,
        facets,
        unfilteredCounts,
        filteredCounts,
        updateFilters,
        removeFilter,
        filterInput
    } = filters || {};

    const allToggleableExclusionReasons: string[] = ['manuallyExcluded', 'status', 'missingData', 'unmatchedSegments'];
    const missingDataReason = 'missingData';
    const fastQAReason = 'unmatchedSegments';
    const statusReason = 'status';

    const isMissingDataActive = showMissingData ?? false;
    const isFastQAActive = showFastFillQA ?? false;
    const isStatusActive = showStatus ?? false;

    const currentExclusions = selectedFilters?.excluded ?? [];
    const areAllExclusionsActive = allToggleableExclusionReasons.every(reason => currentExclusions.includes(reason));

    const statusCount = filteredCounts?.filtered?.status ?? 0;

    const handleExcludedBadgeClick = useCallback(() => {
        if (!updateFilters) {
            console.warn('Attempted to toggle all excluded filters before filter function was ready.');
            return;
        }

        if (areAllExclusionsActive) {
            updateFilters((prev: SelectedFilters) => ({
                ...prev,
                excluded: (prev.excluded ?? []).filter(r => !allToggleableExclusionReasons.includes(r)),
            }));
        } else {
            updateFilters((prev: SelectedFilters) => ({
                ...prev,
                excluded: [...new Set([...(prev.excluded ?? []), ...allToggleableExclusionReasons])],
            }));
        }
    }, [areAllExclusionsActive, updateFilters, allToggleableExclusionReasons]);

    const handleMissingDataToggle = useCallback(() => {
        if (!updateFilters || !setShowMissingData) {
            console.warn('Attempted to toggle missing data before filter function or context setter was ready.');
            return;
        }

        const nextShowMissingData = !isMissingDataActive;

        setShowMissingData(nextShowMissingData);

        updateFilters((prev: SelectedFilters) => {
            const currentExcluded = prev.excluded ?? [];
            let newExcluded: string[];

            if (nextShowMissingData) {
                newExcluded = currentExcluded.includes(missingDataReason)
                    ? currentExcluded
                    : [...currentExcluded, missingDataReason];
            } else {
                newExcluded = currentExcluded.filter((r) => r !== missingDataReason);
            }

            return {
                ...prev,
                excluded: newExcluded,
            };
        });
    }, [updateFilters, setShowMissingData, isMissingDataActive]);

    const handleFastFillToggle = useCallback(() => {
        if (!updateFilters || !setShowFastFillQA) {
            console.warn('Attempted to toggle Fast Fill QA before filter function or context setter was ready.');
            return;
        }
        const nextShowFastFillQA = !isFastQAActive;
        setShowFastFillQA(nextShowFastFillQA);
        updateFilters((prev: SelectedFilters) => {
            const currentExcluded = prev.excluded ?? [];
            let newExcluded: string[];
            if (nextShowFastFillQA) {
                newExcluded = currentExcluded.includes(fastQAReason)
                    ? currentExcluded
                    : [...currentExcluded, fastQAReason];
            } else {
                newExcluded = currentExcluded.filter((r) => r !== fastQAReason);
            }
            return { ...prev, excluded: newExcluded };
        });
    }, [updateFilters, setShowFastFillQA, isFastQAActive]);

    const handleStatusToggle = useCallback(() => {
        if (!updateFilters || !setShowStatus) {
            console.warn('Attempted to toggle status before filter function or context setter was ready.');
            return;
        }
        const nextShowStatus = !isStatusActive;
        setShowStatus(nextShowStatus);
        
        const NON_FINAL_STATUSES = ['draft', 'qa', 'archived'];
        
        updateFilters((prev: SelectedFilters) => {
            const currentStatus = (prev.status ?? []).flatMap(s => s.split(',')).map(s => s.toLowerCase());
            const hasAllNonFinal = NON_FINAL_STATUSES.every(status => currentStatus.includes(status));
            
            let newStatus: string[];
            if (nextShowStatus) {
                // Add non-final statuses if not already present
                newStatus = Array.from(new Set([...currentStatus, ...NON_FINAL_STATUSES]));
            } else {
                // Remove non-final statuses
                newStatus = currentStatus.filter(status => !NON_FINAL_STATUSES.includes(status));
            }
            
            return {
                ...prev,
                status: newStatus.length > 0 ? [newStatus.join(',')] : [],
                excluded: (prev.excluded ?? []).filter(r => r.toLowerCase() !== 'status'),
            };
        });
    }, [updateFilters, setShowStatus, isStatusActive]);


    const exposedFacetForSection = useMemo((): FilterTypes | undefined => {
        switch (groupMode) {
            case 'location': return 'space';
            case 'element': return 'element';
            case 'test': return 'testSuite';
            case 'rating': return 'rating';
            case 'category': return 'testSuite';
            default: return undefined;
        }
    }, [groupMode]);

    if (!facets || !selectedFilters || !updateFilters || !removeFilter || !filteredCounts || !unfilteredCounts) {
    }

    return (

        <div className='flex flex-col gap-2'>
            <ToolbarSlot>
                <FilterSection
                    showSearch={false}
                    exposedFacet={exposedFacetForSection}
                    facets={facets}
                    selectedFilters={selectedFilters}
                    updateFilters={updateFilters}
                    removeFilter={removeFilter}
                    filteredCounts={filteredCounts}
                    unfilteredCounts={unfilteredCounts}
                />

            </ToolbarSlot>
            <ToolbarSlot>
                <EditModeComponent>
                    <div className="flex items-center">
                        <BulkEditDropdown
                            filteredData={filteredData}
                        />
                    </div>
                </EditModeComponent>

                <FilterInput
                    onFilterChange={handleFilterChange}
                    placeholder="Search..."
                    value={filterInput}
                />

                <div className="flex-1"></div>

                <EditModeComponent>
                    {/* <ExcludedTestsBadge
                        counts={filteredCounts?.filtered}
                        onClick={handleExcludedBadgeClick}
                        isActive={areAllExclusionsActive}
                    /> */}
                    <div className="flex flex-row gap-2">
                        <TestErrorPillButton
                            status={'missing'}
                            size="sm"
                            value={filteredCounts?.filtered?.missingData ?? missingDataCount}
                            onClick={handleMissingDataToggle}
                            isActive={isMissingDataActive}
                            disabled={(filteredCounts?.filtered?.missingData ?? missingDataCount) === 0}
                            //The Test includes compulsory data fields that have not been completed.
                            tooltip={`Report contains ${filteredCounts?.filtered?.missingData ?? missingDataCount} tests that include compulsory data fields that have not been completed`}
                        />
                        <TestErrorPillButton
                            status={'fastfill'}
                            size="sm"
                            value={filteredCounts?.filtered?.unmatchedSegments ?? missingSegments}
                            onClick={handleFastFillToggle}
                            isActive={isFastQAActive}
                            disabled={(filteredCounts?.filtered?.unmatchedSegments ?? missingSegments) === 0}
                            //The Test includes text that does not match a pre-approved Fast Fill response.
                            tooltip={`Report contains ${filteredCounts?.filtered?.unmatchedSegments ?? missingSegments} tests that include text that does not match a pre-approved Fast Fill response`}
                        />

                        <TestErrorPillButton
                            status={'status'}
                            size="sm"
                            value={filteredCounts?.filtered?.status ?? statusCount}
                            onClick={handleStatusToggle}
                            isActive={isStatusActive}
                            disabled={(filteredCounts?.filtered?.status ?? statusCount) === 0}
                            tooltip={`Report contains ${filteredCounts?.filtered?.status ?? statusCount} tests that are currently in Archived, Draft or QA status and have not been saved as Final`}
                        />
                    </div>

                </EditModeComponent>
            </ToolbarSlot>
        </div>
    );
};

export default ReportFilterBar;